import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, type LinkProps, useSearch } from '@tanstack/react-router'
import { useState } from 'react'

import type { AccountIdentity } from '@/bindings/AccountIdentity'
import type { AccountLookup } from '@/bindings/AccountLookup'
import type { AccountLookupRequest } from '@/bindings/AccountLookupRequest'
import type { RestaurantAccount } from '@/bindings/RestaurantAccount'
import type { RestaurantContext } from '@/bindings/RestaurantContext'
import type { RoleAssignment } from '@/bindings/RoleAssignment'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { emptyIdentifierDraft, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { IdentifierSearch } from '@/features/auth/identifier-search'
import { Identity, RevokeButton } from '@/features/back-office/account-parts'
import { backOfficeKeys, branchStaffPath, branchStaffQuery } from '@/features/back-office/api'
import { request } from '@/lib/api'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'
import { ROLE_LABELS, canManageBranch, canManageRestaurant } from '@/lib/roles'

const ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/staff'
const ROUTE_PATH = '/$restaurantSlug/$branchSlug/manage/staff'

/**
 * Branch staff page (PRD, H8): who works in this branch, plus E7's exact lookup to add a
 * registered account as staff here. Admins in scope and above; the backend is the authority.
 */
export function BranchStaffPage() {
  const t = useT()
  const { restaurantId, branchId, context } = useManagedBranch()
  const { page } = useSearch({ from: ROUTE_ID })

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('staff.title')}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
    </div>
  )
  if (!canManageBranch(context.role)) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p role="alert" className="text-muted text-sm">
          {t('staff.adminOnly')}
        </p>
      </Page>
    )
  }

  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      <FindCard context={context} branchId={branchId} />
      <RosterCard restaurantId={restaurantId} branchId={branchId} page={page} />
    </Page>
  )
}

function isStaffHere(assignment: RoleAssignment | null, branchId: string): boolean {
  return assignment?.role === 'staff' && assignment.branches.some((branch) => branch.id === branchId)
}

/** E7's lookup with a one-button verdict: add here, already here, or "another role, see the restaurant page". */
function FindCard({ context, branchId }: { context: RestaurantContext; branchId: string }) {
  const t = useT()
  const restaurantId = context.settings.id
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(() => emptyIdentifierDraft())
  const [found, setFound] = useState<AccountLookup | undefined>()
  const [status, setStatus] = useState<'added' | 'removed' | undefined>()

  const lookup = useMutation({
    mutationFn: () => {
      const body: AccountLookupRequest = { identifier: toLoginIdentifier(draft) }
      return request<AccountLookup>(branchStaffPath(restaurantId, branchId, '/lookup'), { method: 'POST', body })
    },
    onSuccess: (result) => {
      setStatus(undefined)
      setFound(result)
    },
  })

  function search() {
    setFound(undefined)
    lookup.mutate()
  }

  if (!open) {
    return (
      <div className="flex justify-end">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          {t('accounts.find')}
        </Button>
      </div>
    )
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('accounts.find')}</h2>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        <p className="text-muted text-sm">{t('staff.findIntro')}</p>
        <IdentifierSearch
          draft={draft}
          onDraftChange={(next) => {
            setDraft(next)
            if (lookup.error) lookup.reset()
          }}
          onSearch={search}
          pending={lookup.isPending}
          error={lookup.error}
        />
      </div>
      {found && (
        <div className="border-border flex flex-col gap-4 border-t pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Identity user={found.user} />
            {found.assignment ? (
              <Badge tone="success">{t(ROLE_LABELS[found.assignment.role])}</Badge>
            ) : (
              <Badge tone="muted">{t('accounts.noRole')}</Badge>
            )}
          </div>
          {status && (
            <p role="status" className="text-muted text-sm">
              {t(status === 'added' ? 'staff.added' : 'staff.removed')}
            </p>
          )}
          <Standing
            context={context}
            branchId={branchId}
            found={found}
            onAdded={(saved) => {
              setFound({ user: saved.user, assignment: saved.assignment })
              setStatus('added')
            }}
            onRemoved={() => {
              setFound({ user: found.user, assignment: null })
              setStatus('removed')
            }}
          />
        </div>
      )}
    </Card>
  )
}

function Standing({
  context,
  branchId,
  found,
  onAdded,
  onRemoved,
}: {
  context: RestaurantContext
  branchId: string
  found: AccountLookup
  onAdded: (saved: RestaurantAccount) => void
  onRemoved: () => void
}) {
  const t = useT()
  const restaurantId = context.settings.id
  const { user, assignment } = found

  if (assignment && assignment.role !== 'staff') {
    return (
      <p className="text-muted flex flex-wrap gap-x-2 text-sm">
        <span>{t('staff.otherRole', { role: t(ROLE_LABELS[assignment.role]) })}</span>
        {canManageRestaurant(context.role) && (
          <Link
            to="/$restaurantSlug/manage/accounts"
            params={{ restaurantSlug: context.settings.slug }}
            search={{ page: 1, role: assignment.role }}
            className="text-accent-ink font-medium underline"
          >
            {t('nav.accounts')}
          </Link>
        )}
      </p>
    )
  }
  if (isStaffHere(assignment, branchId)) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-muted text-sm">{t('staff.here')}</p>
        <RemoveButton restaurantId={restaurantId} branchId={branchId} user={user} onRemoved={onRemoved} />
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      {assignment && <OtherBranches assignment={assignment} branchId={branchId} />}
      {!assignment && <p className="text-muted text-sm">{t('accounts.grantNotice')}</p>}
      <AddButton restaurantId={restaurantId} branchId={branchId} user={user} onAdded={onAdded} />
    </div>
  )
}

function AddButton({
  restaurantId,
  branchId,
  user,
  onAdded,
}: {
  restaurantId: string
  branchId: string
  user: AccountIdentity
  onAdded: (saved: RestaurantAccount) => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const add = useMutation({
    mutationFn: () => request<RestaurantAccount>(branchStaffPath(restaurantId, branchId, `/${user.id}`), { method: 'PUT' }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: backOfficeKeys.branchStaff(restaurantId, branchId) })
      onAdded(saved)
    },
  })
  return (
    <div className="flex flex-col gap-2">
      <FormError error={add.error} />
      <Button loading={add.isPending} className="self-start" onClick={() => add.mutate()}>
        {t('staff.add')}
      </Button>
    </div>
  )
}

function RemoveButton({
  restaurantId,
  branchId,
  user,
  onRemoved,
}: {
  restaurantId: string
  branchId: string
  user: AccountIdentity
  onRemoved: () => void
}) {
  return (
    <RevokeButton
      path={branchStaffPath(restaurantId, branchId, `/${user.id}`)}
      queryKey={backOfficeKeys.branchStaff(restaurantId, branchId)}
      user={user}
      label="staff.remove"
      confirm="staff.confirmRemove"
      confirmOf="staff.confirmRemoveOf"
      onRevoked={onRemoved}
    />
  )
}

/** The staff's other branches: what stays when they are removed here. */
function OtherBranches({ assignment, branchId }: { assignment: RoleAssignment; branchId: string }) {
  const t = useT()
  const others = assignment.branches.filter((branch) => branch.id !== branchId)
  if (others.length === 0) return null
  return (
    <p className="text-muted flex flex-wrap gap-x-3 text-sm">
      <span>{t('staff.alsoIn')}</span>
      {others.map((branch) => (
        <span key={branch.id}>{branch.name}</span>
      ))}
    </p>
  )
}

function RosterCard({ restaurantId, branchId, page }: { restaurantId: string; branchId: string; page: number }) {
  const t = useT()
  const list = useQuery(branchStaffQuery(restaurantId, branchId, page))
  const { restaurantSlug, branchSlug } = useManagedBranch()
  const params = { restaurantSlug, branchSlug }
  const link = (page: number): LinkProps => ({ to: ROUTE_PATH, params, search: { page } })
  useClampedPage(page, list.data, link)

  return (
    <Card className="flex flex-col gap-4">
      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data &&
        (list.data.items.length === 0 ? (
          <p className="text-muted text-sm">{t('staff.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((account) => (
              <li key={account.user.id} className="border-border flex flex-col gap-3 rounded-lg border p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <Identity user={account.user} />
                  <RemoveButton restaurantId={restaurantId} branchId={branchId} user={account.user} onRemoved={() => {}} />
                </div>
                <OtherBranches assignment={account.assignment} branchId={branchId} />
              </li>
            ))}
          </ul>
        ))}
      <Pager label={t('staff.pager')} page={page} total={list.data?.total ?? 0} link={link} />
    </Card>
  )
}
